"use client";


import {
  FC,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import dayjs from "dayjs";
import { useRouter } from "next/navigation";
import { shutdown } from "@intercom/messenger-js-sdk";

import type {
  AccessToken,
  ILoginSuccessResponse,
  VerifyLoginPasscodeDto,
} from "@auth/types";
import { AuthSDK } from "@auth/client-sdk";
import { useUserStore } from "libs/state/src/lib/stores/useUserStore";
import { AUTH_COOKIES } from "@/constants/auth";

import { useDatadog } from "./useDatadog";
import { IAuthContextProps } from "./types";
import { authContext } from "./auth-context";
import { extractIdentifiers } from "./utils";
import { AuthProviderAPI } from "./AuthProviderAPI";

const MAX_REFRESH_INTERVAL_MINUTES = 15;

const getCookieValue = (cookieName: string): string | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }
  const regex = new RegExp(`(^| )${cookieName}=([^;]+)`);
  const match = regex.exec(document.cookie);
  return match?.[2];
};

export const AuthProvider: FC<IAuthContextProps> = ({
  children,
  authConfig,
  initialLoggedIn = false,
  onLogout,
}) => {

  const [ accessToken, setAccessToken ] = useState<AccessToken | undefined>();
  const [ loggedIn, setLoggedIn ] = useState(initialLoggedIn);

  const refreshTimeoutRef = useRef<number | null>(null);
  const refreshIntervalRef = useRef<number | null>(null);
  const isRefreshingRef = useRef(false);
  const scheduleRefreshRef = useRef<((token: string) => void) | null>(null);

  const { unsetUserDetails } = useUserStore();
  const datadog = useDatadog();
  const router = useRouter();

  const auth = useMemo(() => new AuthSDK(authConfig.AUTH_API_BASE_URL), [
    authConfig.AUTH_API_BASE_URL,
  ]);

  const authAPI = useMemo(() => new AuthProviderAPI(authConfig.NEXT_PUBLIC_WEBSITE_URL ?? ""), [
    authConfig.NEXT_PUBLIC_WEBSITE_URL,
  ]);

  const clearAllTimers = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }
  }, []);

  const resetSession = useCallback(async () => {
    setAccessToken(undefined);
    setLoggedIn(false);
    unsetUserDetails();
    clearAllTimers();
    shutdown();

    try {
      await authAPI.endSession();
    } catch (err) {
      console.error("Failed to clear session cookies on server:", err);
    }
  }, [ unsetUserDetails, clearAllTimers, authAPI ]);

  const logout = useCallback(async () => {
    await resetSession();

    if (onLogout) {
      onLogout();
    }
    window.location.href = "/login";
  }, [ resetSession, onLogout ]);

  const doAccessTokenRefresh = useCallback(async () => {
    if (isRefreshingRef.current) { return; }
    isRefreshingRef.current = true;

    try {
      const response = await authAPI.refreshSession();
      if (response?.status === "no_refresh_needed") { return; }

      const latestAccessToken = getCookieValue(AUTH_COOKIES.ACCESS_TOKEN);
      setAccessToken(latestAccessToken);
      setLoggedIn(!!latestAccessToken);

      if (latestAccessToken) {
        scheduleRefreshRef.current?.(latestAccessToken);
      } else {
        await logout();
      }
    } catch (err) {
      console.error("Access token refresh failed", err);
      await logout();
    } finally {
      isRefreshingRef.current = false;
    }

  }, [ logout, authAPI ]);

  const scheduleRefresh = useCallback((token: string) => {
    const tokenData = AuthSDK.decodePayload(token);
    if (!tokenData?.exp) { return; }

    clearAllTimers();

    const now = dayjs().unix();
    const refreshIn = Math.min(tokenData.exp - now - 60, 60 * MAX_REFRESH_INTERVAL_MINUTES); // 60 seconds before expiry, max 15min

    if (refreshIn > 0) {
      refreshTimeoutRef.current = window.setTimeout(() => {
        void doAccessTokenRefresh();
      }, 1000 * refreshIn);
    } else {
      void doAccessTokenRefresh();
    }
  }, [ doAccessTokenRefresh, clearAllTimers ]);

  scheduleRefreshRef.current = scheduleRefresh;

  const setLoginTokens = useCallback(async (res: ILoginSuccessResponse, rememberMe = true) => {
    try {
      const payload = AuthSDK.decodePayload(res.access_token);
      if (!payload) {
        throw new Error("Failed to decode access token payload");
      }

      const kkCustomerId = payload.old ?? payload.iold;

      await authAPI.startSession({
        accessToken: res.access_token,
        refreshToken: res.refresh_token,
        kkCustomerId: kkCustomerId ?? undefined,
        accessTokenMaxAge: res.expires_in,
        kkCustomerIdMaxAge: res.expires_in,
        rememberMe,
      });

      const latestAccessToken = getCookieValue(AUTH_COOKIES.ACCESS_TOKEN);

      if (!latestAccessToken) {
        throw new Error("Session cookie was not set after startSession — login cannot proceed");
      }

      setAccessToken(latestAccessToken);
      setLoggedIn(true);
      scheduleRefreshRef.current?.(latestAccessToken);
    } catch (err) {
      console.error("Failed to set session cookies", err);
      throw err;
    }
  }, [ authAPI ]);

  const login = useCallback(async (email: string, password: string, rememberMe = true, returnTo?: string) => {
    const res = await auth.loginWithEmailPassword(email.toLowerCase(), password);
    await setLoginTokens(res, rememberMe);
    router.push(returnTo ?? "/");
  }, [ auth, setLoginTokens, router ]);

  const sendMagicLink = useCallback(
    async (email: string, redirectTo?: string) => auth.sendMagicLink(email.toLowerCase(), redirectTo),
    [ auth ],
  );

  const sendPasscode = useCallback(async (email: string, redirectTo?: string) => {
    await auth.sendPasscode(email.toLowerCase(), redirectTo);
  }, [ auth ]);

  const verifyPasscode = useCallback(async ({ email, passcode }
  : VerifyLoginPasscodeDto) => auth.verifyPasscode({ email: email.toLowerCase(), passcode }), [ auth ]);

  const issueChatToken = useCallback(async (phoneNumber: string, name: string) => {
    if (!accessToken) {
      throw new Error("Cannot issue chat token: No access token available");
    }
    return auth.getChatToken(accessToken, phoneNumber, name);
  }, [ auth, accessToken ]);

  const forceLogin = useCallback(async (tokenData: ILoginSuccessResponse | undefined, rememberMe = true) => {
    await resetSession();
    if (tokenData) {
      await setLoginTokens(tokenData, rememberMe);
    }
  }, [ resetSession, setLoginTokens ]);

  const identifiers = useMemo(() => {
    if (!accessToken) { return {}; }
    const extracted = extractIdentifiers(accessToken);

    datadog?.setGlobalContextProperty("cx_user", extracted);
    if (extracted.email) {
      datadog?.setGlobalContextProperty("kk_email", extracted.email);
    }
    if (extracted.iold || extracted.old) {
      datadog?.setGlobalContextProperty("kk_customer_id", extracted.iold ?? extracted.old);
    }
    if (extracted.id) {
      datadog?.setGlobalContextProperty("token_sub", extracted.id);
    }

    return extracted;
  }, [ accessToken, datadog ]);

  useEffect(() => {
    const cookieAccessToken = getCookieValue(AUTH_COOKIES.ACCESS_TOKEN);

    if (!cookieAccessToken) {
      if (initialLoggedIn || accessToken !== undefined) {
        void doAccessTokenRefresh();
      } else {
        setLoggedIn(false);
      }
      return clearAllTimers;
    }

    try {
      const tokenData = AuthSDK.decodePayload(cookieAccessToken);
      if (!tokenData?.exp) {
        throw new Error("Access token missing exp claim");
      }

      if (accessToken !== cookieAccessToken) {
        setAccessToken(cookieAccessToken);
        setLoggedIn(true);
      }

      const now = dayjs().unix();
      const isTokenValid = tokenData.exp > now;

      if (isTokenValid) {
        scheduleRefreshRef.current?.(cookieAccessToken);
      } else {
        void doAccessTokenRefresh();
      }
    } catch (err) {
      console.error("Invalid access token", err);
      void logout();
    }

    return clearAllTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ doAccessTokenRefresh, logout, clearAllTimers ]);

  const value = useMemo(() => ({
    loggedIn,
    login,
    sendMagicLink,
    accessToken,
    setLoginTokens,
    logout,
    resetSession,
    forceLogin,
    doAccessTokenRefresh,
    email: identifiers.email,
    firstName: identifiers.firstName,
    lastName: identifiers.lastName,
    id: identifiers.id,
    iold: identifiers.iold,
    old: identifiers.old,
    sendPasscode,
    verifyPasscode,
    issueChatToken,
  }), [
    loggedIn,
    login,
    sendMagicLink,
    accessToken,
    setLoginTokens,
    logout,
    resetSession,
    forceLogin,
    doAccessTokenRefresh,
    identifiers,
    sendPasscode,
    verifyPasscode,
    issueChatToken,
  ]);

  return (
    <authContext.Provider value={ value }>
      { children }
    </authContext.Provider>
  );
};
